'use client';

import { Fragment, forwardRef } from 'react';
import { PROJECT_NAME } from '@shared/constants';
import { Camera, CheckCircle2, ShieldCheck, Stethoscope } from 'lucide-react';
import { PublicSection } from '../../../../components/public/PublicSection';

interface HowItWorksStep {
  title: string;
  description: string;
  icon: typeof Camera;
}

const HOW_IT_WORKS_STEPS: HowItWorksStep[] = [
  {
    title: 'Start with AI facial scanning',
    description:
      'Open the app and complete a quick AI facial scan to begin your consultation journey in seconds.',
    icon: Camera,
  },
  {
    title: 'Verify and continue securely',
    description:
      'Your details are confirmed in-app so your consultation can proceed quickly and securely.',
    icon: CheckCircle2,
  },
  {
    title: 'Consult with a licensed clinician',
    description:
      'Connect remotely with an HPCSA-registered practitioner for a secure, high-quality consultation.',
    icon: Stethoscope,
  },
  {
    title: 'Get documents or book follow-up care',
    description:
      'Receive your medical documents in-app and book a follow-up appointment when you need continued support.',
    icon: ShieldCheck,
  },
];

const STEP_ACCENT_STYLES = [
  'from-brand-teal to-brand-teal/70',
  'from-brand-teal/90 to-brand-mint',
  'from-brand-teal to-primary/80',
  'from-primary to-brand-teal/80',
];

/**
 * @group Landing
 * @summary How it works section
 */
export const HowItWorksSection = forwardRef<HTMLElement>((props, ref) => {
  return (
    <PublicSection
      ref={ref}
      id="how-phila-works"
      className="relative overflow-hidden bg-brand-mint"
      heading={`${PROJECT_NAME} in 4 Simple Steps`}
      subheading="How Phila Works"
      headingColor="#084349"
      subheadingColor="#084349"
      separatorColor="#084349"
      separatorLineColor="hsl(var(--brand-teal) / 0.4)"
      indented={false}
      style={{
        boxShadow: 'inset 0 12px 24px rgba(0, 0, 0, 0.4), inset 0 6px 12px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(0, 0, 0, 0.2)',
      }}
    >
      <div className="mx-auto max-w-4xl">

        <div className="space-y-0">
        {HOW_IT_WORKS_STEPS.map((step, index) => {
          const Icon = step.icon;
          const accentStyle = STEP_ACCENT_STYLES[index % STEP_ACCENT_STYLES.length];
          const isLast = index === HOW_IT_WORKS_STEPS.length - 1;

          return (
            <Fragment key={step.title}>
              <article
                className="group relative overflow-hidden rounded-2xl border border-brand-teal/20 bg-white/80 p-5 shadow-[0_8px_30px_rgba(8,67,73,0.08)] backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(8,67,73,0.15)] sm:p-6"
              >
                <div className={`absolute inset-y-0 left-0 w-1 bg-gradient-to-b ${accentStyle}`} />
                <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-brand-teal/10 blur-2xl transition-opacity duration-300 group-hover:opacity-100" />

                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal ring-1 ring-brand-teal/15">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="rounded-full bg-brand-teal px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                    Step {index + 1}
                  </span>
                </div>
                <h3 className="mb-2 text-lg font-semibold text-[#084349] sm:text-xl">
                  {step.title}
                </h3>
                <p className="max-w-3xl text-sm leading-relaxed text-[#084349]/85 sm:text-base">
                  {step.description}
                </p>

                <span className="pointer-events-none absolute right-4 top-3 text-5xl font-bold text-brand-teal/10 sm:text-6xl">
                  0{index + 1}
                </span>
              </article>

              {!isLast && (
                <div className="flex h-8 items-center pl-6">
                  <div className="h-full border-l-2 border-dashed border-brand-teal/25" />
                </div>
              )}
            </Fragment>
          );
        })}
        </div>
      </div>
    </PublicSection>
  );
});

HowItWorksSection.displayName = 'HowItWorksSection';
